import { Container, Divider, Stack, Typography, TextField, InputAdornment, Chip, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, FormControlLabel, Switch } from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { themeOrange } from "../../utils/colorTheme";
import { MdSearch, MdAdd } from "react-icons/md";
import SubscriptionsTable from "../../component/common/customTable/SubscriptionsTable";
import { getSubscriptionsAsync, cancelSubscriptionAsync, addSubscriptionAsync, createRealSubscriptionAsync } from "../../services/AdminServiceManage";
import { get_users_list_by_role } from "../../services/DashboardUsers";
import { success_toast_message, error_toast_message } from "../../utils/toast_message";

const DashboardSubscriptions = () => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [cancellingId, setCancellingId] = useState(null);

  // Add-customer modal state
  const [addOpen, setAddOpen] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [pickedUser, setPickedUser] = useState(null);
  const [chargeViaStripe, setChargeViaStripe] = useState(false);
  const [planName, setPlanName] = useState("Membership");
  const [expiryDate, setExpiryDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10); // default 1 year out
  });
  const [notes, setNotes] = useState("");

  const { get_users_list_by_role_data } = useSelector((state) => state.dashboard_users);

  // Users eligible for manual grant = customers (role 3) without an active subscription yet
  const candidateUsers = useMemo(() => {
    const list = Array.isArray(get_users_list_by_role_data) ? get_users_list_by_role_data : [];
    const subscribedIds = new Set(subscriptions.map((s) => s?.user?.id).filter(Boolean));
    return list.filter((u) => !subscribedIds.has(u.id));
  }, [get_users_list_by_role_data, subscriptions]);

  // Fetch subscriptions on mount
  useEffect(() => {
    fetchSubscriptions();
    dispatch(get_users_list_by_role(3)); // preload customer list for the add modal
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const result = await dispatch(getSubscriptionsAsync()).unwrap();
      if (result?.subscriptions) {
        setSubscriptions(result.subscriptions);
      }
    } catch (error) {
      console.error("Failed to fetch subscriptions:", error);
      // Show empty state instead of error - backend endpoint may not be deployed yet
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const resetAddForm = () => {
    setPickedUser(null);
    setChargeViaStripe(false);
    setPlanName("Membership");
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    setExpiryDate(d.toISOString().slice(0, 10));
    setNotes("");
  };

  const handleAddSubscription = async () => {
    if (!pickedUser) {
      error_toast_message("Please pick a customer");
      return;
    }
    setAddSaving(true);
    try {
      let result;
      if (chargeViaStripe) {
        // Real Stripe subscription — charges saved card $10 CAD now, recurring monthly.
        if (!window.confirm(`Charge ${pickedUser.first_name || pickedUser.email} $10 CAD now and start auto-renewal?`)) {
          setAddSaving(false);
          return;
        }
        result = await dispatch(createRealSubscriptionAsync({ user_id: pickedUser.id })).unwrap();
      } else {
        // Manual grant — no charge.
        const payload = {
          user_id: pickedUser.id,
          plan_name: planName || "Membership",
          expiry_date: expiryDate || null,
          notes: notes || null,
        };
        result = await dispatch(addSubscriptionAsync(payload)).unwrap();
      }
      if (result?.status) {
        success_toast_message(chargeViaStripe ? "Charged & subscribed" : "Membership granted");
        if (result.subscription) {
          setSubscriptions((prev) => [result.subscription, ...prev]);
        } else {
          await fetchSubscriptions();
        }
        setAddOpen(false);
        resetAddForm();
      } else {
        error_toast_message(result?.message || "Failed to add subscription");
      }
    } catch (error) {
      error_toast_message(error?.message || "Failed to add subscription");
    } finally {
      setAddSaving(false);
    }
  };

  const handleCancelSubscription = async (userId) => {
    if (!window.confirm("Are you sure you want to cancel this subscription?")) {
      return;
    }

    setCancellingId(userId);
    try {
      const result = await dispatch(cancelSubscriptionAsync(userId)).unwrap();
      if (result?.status) {
        success_toast_message("Subscription cancelled successfully");
        // Remove from local state
        setSubscriptions((prev) => prev.filter((sub) => sub.user?.id !== userId));
      } else {
        error_toast_message(result?.message || "Failed to cancel subscription");
      }
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      error_toast_message(error?.message || "Failed to cancel subscription");
    } finally {
      setCancellingId(null);
    }
  };

  const filteredSubscriptions = subscriptions.filter((sub) => {
    const matchesSearch =
      sub?.user?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub?.user?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub?.user?.email?.toLowerCase().includes(searchTerm.toLowerCase());

    if (filterStatus === "all") return matchesSearch;
    if (filterStatus === "active") return matchesSearch && sub?.status === "active";
    if (filterStatus === "expired") return matchesSearch && sub?.status === "expired";
    return matchesSearch;
  });

  return (
    <Stack bgcolor={"#FAFAFA"}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={1}
        my={1}
      >
        <Typography className="mainHeading">Subscriptions</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems={{ xs: "stretch", sm: "center" }}>
          <TextField
            size="small"
            placeholder="Search subscribers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch color="#666" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "white",
              },
            }}
          />
          <Button
            variant="contained"
            startIcon={<MdAdd />}
            onClick={() => setAddOpen(true)}
            sx={{
              backgroundColor: themeOrange,
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#d94f0f" },
              whiteSpace: "nowrap",
            }}
          >
            Add Customer
          </Button>
        </Stack>
      </Stack>
      <Divider />

      <Container
        sx={{ maxWidth: { xl: "xl", lg: "lg", md: "md", sm: "sm", xs: "xs" } }}
      >
        <Stack my={2}>
          <Stack direction={"row"} gap={1} mb={2}>
            <Chip
              label="All"
              onClick={() => setFilterStatus("all")}
              sx={{
                backgroundColor: filterStatus === "all" ? themeOrange : "#e0e0e0",
                color: filterStatus === "all" ? "white" : "#666",
                "&:hover": { backgroundColor: filterStatus === "all" ? themeOrange : "#d0d0d0" },
              }}
            />
            <Chip
              label="Active"
              onClick={() => setFilterStatus("active")}
              sx={{
                backgroundColor: filterStatus === "active" ? "#22c55e" : "#e0e0e0",
                color: filterStatus === "active" ? "white" : "#666",
                "&:hover": { backgroundColor: filterStatus === "active" ? "#22c55e" : "#d0d0d0" },
              }}
            />
            <Chip
              label="Expired"
              onClick={() => setFilterStatus("expired")}
              sx={{
                backgroundColor: filterStatus === "expired" ? "#ef4444" : "#e0e0e0",
                color: filterStatus === "expired" ? "white" : "#666",
                "&:hover": { backgroundColor: filterStatus === "expired" ? "#ef4444" : "#d0d0d0" },
              }}
            />
          </Stack>

          {loading ? (
            <Stack
              sx={{ height: "30vh" }}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <CircularProgress sx={{ color: themeOrange }} />
            </Stack>
          ) : filteredSubscriptions.length === 0 ? (
            <Stack
              sx={{ height: "30vh" }}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Typography className="mainPara" sx={{ color: "#666" }}>
                No subscriptions found
              </Typography>
            </Stack>
          ) : (
            <SubscriptionsTable
              subscriptions={filteredSubscriptions}
              onCancelSubscription={handleCancelSubscription}
              cancellingId={cancellingId}
            />
          )}
        </Stack>
      </Container>

      {/* Add-customer-to-subscription modal */}
      <Dialog
        open={addOpen}
        onClose={() => !addSaving && setAddOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Add customer to subscription</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <Autocomplete
              options={candidateUsers}
              value={pickedUser}
              onChange={(_e, v) => setPickedUser(v)}
              getOptionLabel={(u) =>
                u ? `${u.first_name || ""} ${u.last_name || ""} — ${u.email || ""}`.trim() : ""
              }
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer"
                  placeholder="Search by name or email"
                  size="small"
                />
              )}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={chargeViaStripe}
                  onChange={(e) => setChargeViaStripe(e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Stack>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                    Charge $10 CAD via Stripe (recurring monthly)
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#666" }}>
                    Customer must have a saved card on file. Card is charged immediately.
                  </Typography>
                </Stack>
              }
              sx={{ alignItems: "flex-start", m: 0 }}
            />

            {!chargeViaStripe && (
              <>
                <TextField
                  label="Plan name"
                  size="small"
                  value={planName}
                  onChange={(e) => setPlanName(e.target.value)}
                  placeholder="Membership"
                />
                <TextField
                  label="Expiry date"
                  size="small"
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave blank for lifetime (no expiry)"
                />
                <TextField
                  label="Notes (optional)"
                  size="small"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  minRows={2}
                  placeholder="Reason, reference, etc."
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)} disabled={addSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSubscription}
            disabled={addSaving || !pickedUser}
            sx={{ backgroundColor: themeOrange, "&:hover": { backgroundColor: "#d94f0f" } }}
          >
            {addSaving ? <CircularProgress size={18} color="inherit" /> : (chargeViaStripe ? "Charge $10 & subscribe" : "Grant membership")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default DashboardSubscriptions;
